import React, { useEffect, useState } from "react";
import { Loader2, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, aiJob } from "@/lib/api";
import { PageHeader, SectionCard, StatusPill } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { AIOutputDisclaimer } from "@/components/Disclaimer";
import AIFeedback from "@/components/AIFeedback";
import ShowReasoning from "@/components/ShowReasoning";
import { exportToPDF } from "@/lib/pdf";

export default function RevenueCycle() {
  const [claims, setClaims] = useState([]);
  const [patients, setPatients] = useState([]);
  const [filter, setFilter] = useState({ payer: "", status: "" });
  const [busy, setBusy] = useState(false);
  const [appeal, setAppeal] = useState(null);

  const load = () => api.get("/claims").then((r)=>setClaims(r.data));
  useEffect(() => { load(); api.get("/patients").then((r)=>setPatients(r.data)); }, []);

  const generateAppeal = async (id) => {
    setBusy(true);
    try {
      const data = await aiJob(`/claims/${id}/generate-appeal`, {});
      setAppeal({ id, text: data.final, score: data.score, reasoning: data.reasoning, verification_footer: data.verification_footer });
      toast.success("Appeal letter drafted");
      load();
    } catch { toast.error("Appeal generation failed"); }
    finally { setBusy(false); }
  };

  const filtered = claims.filter((c) =>
    (!filter.payer || c.payer_name === filter.payer) &&
    (!filter.status || c.status === filter.status)
  );

  const payers = [...new Set(claims.map(c=>c.payer_name))];
  const stats = {
    submitted: claims.filter(c=>c.status==="submitted").length,
    paid: claims.filter(c=>c.status==="paid").length,
    denied: claims.filter(c=>c.status==="denied").length,
    underpaid: claims.filter(c=>c.underpaid).length,
  };

  const columns = [
    { key: "claim_number", label: "Claim #", render: (r)=><span className="font-mono text-xs">{r.claim_number}</span> },
    { key: "patient_id", label: "Patient", render: (r)=>patients.find(p=>p.id===r.patient_id)?.name || "—" },
    { key: "payer_name", label: "Payer" },
    { key: "billed_amount", label: "Billed", render: (r)=>`$${r.billed_amount.toFixed(2)}` },
    { key: "paid_amount", label: "Paid", render: (r)=>(
      <span className={r.underpaid?"text-amber-700 font-medium":""}>${r.paid_amount.toFixed(2)} {r.underpaid && <AlertTriangle className="w-3.5 h-3.5 inline ml-1" />}</span>
    ) },
    { key: "status", label: "Status", render: (r)=><StatusPill status={r.status} /> },
    { key: "denial_reason", label: "Denial Reason", render: (r)=>r.denial_reason || "—" },
    { key: "actions", label: "Action", sortable: false, render: (r) => r.status === "denied" ? (
      <button onClick={()=>generateAppeal(r.id)} className="text-xs px-2 py-1 rounded bg-brand-primary text-white hover:bg-[#35096B]" data-testid={`appeal-${r.id}`}>Draft Appeal</button>
    ) : "—" },
  ];

  return (
    <>
      <PageHeader eyebrow="Module 3" title="Revenue Cycle Command"
        subtitle="Claims tracking, denial management with topology-drafted appeals, and underpayment detection." testid="rcm-page" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat label="Submitted" value={stats.submitted} />
        <Stat label="Paid" value={stats.paid} />
        <Stat label="Denied" value={stats.denied} accent="red" />
        <Stat label="Underpaid" value={stats.underpaid} accent="amber" />
      </div>

      <SectionCard title="Claims" testid="claims-section"
        actions={
          <div className="flex gap-2">
            <select value={filter.payer} onChange={(e)=>setFilter({...filter, payer:e.target.value})}
              className="text-xs px-2 py-1.5 border border-brand rounded-md bg-white" data-testid="filter-payer">
              <option value="">All payers</option>
              {payers.map(p=><option key={p}>{p}</option>)}
            </select>
            <select value={filter.status} onChange={(e)=>setFilter({...filter, status:e.target.value})}
              className="text-xs px-2 py-1.5 border border-brand rounded-md bg-white" data-testid="filter-status">
              <option value="">All status</option>
              <option value="submitted">Submitted</option>
              <option value="paid">Paid</option>
              <option value="denied">Denied</option>
            </select>
          </div>
        }>
        <DataTable data={filtered} columns={columns} searchKeys={["claim_number","payer_name","denial_reason"]} testid="claims-table" />
      </SectionCard>

      {(busy || appeal) && (
        <SectionCard title="Appeal Letter Draft"
          actions={appeal && <button onClick={()=>exportToPDF("Claim Appeal Letter", appeal.text)} className="text-xs px-3 py-1.5 rounded-md border border-brand hover:bg-brand-bg inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> PDF</button>}>
          {busy && <div className="flex items-center gap-2 text-sm text-brand-muted"><Loader2 className="w-4 h-4 animate-spin" /> Topology agent drafting appeal…</div>}
          {!busy && appeal && (
            <>
              <AIOutputDisclaimer />
              <pre className="text-sm whitespace-pre-wrap leading-relaxed text-brand-text max-h-96 overflow-y-auto" data-testid="appeal-text">{appeal.text}</pre>
              {appeal.verification_footer && (
                <div className="mt-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-900 italic" data-testid="appeal-verification-footer">{appeal.verification_footer}</div>
              )}
              <ShowReasoning reasoning={appeal.reasoning} score={appeal.score} testid="appeal-reasoning" />
              <AIFeedback module="revenue-cycle" outputId={appeal.id} outputType="appeal_letter" testid="appeal-feedback" />
            </>
          )}
        </SectionCard>
      )}
    </>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="kpi-card">
    <div className="eyebrow">{label}</div>
    <div className={`text-3xl font-semibold mt-2 ${accent==="red"?"text-red-600":accent==="amber"?"text-amber-600":"text-brand-text"}`}>{value}</div>
  </div>
);
